import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuthHeaders } from "../auth/apiClient";
import type { UserRole } from "../auth/AuthContext";

export interface CockpitUser {
  id: number;
  username: string;
  email: string | null;
  full_name: string | null;
  role: UserRole;
  tenant_id: number;
  is_active: boolean;
  must_change_password: boolean;
  last_login_at: number | null;
  created_at: number;
}

export function useUsers() {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ["settings", "users"],
    queryFn: async () => {
      const res = await fetch("/api/cockpit/settings/users", { headers });
      if (!res.ok) throw new Error("Users load failed");
      return res.json() as Promise<{ users: CockpitUser[] }>;
    },
    staleTime: 60_000,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  const headers = useAuthHeaders(true);
  return useMutation({
    mutationFn: async (payload: { username: string; full_name: string; email?: string | null; role: UserRole }) => {
      const res = await fetch("/api/cockpit/settings/users", {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("User create failed");
      return res.json() as Promise<{ user: CockpitUser; temporary_password: string }>;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["settings", "users"] }),
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  const headers = useAuthHeaders(true);
  return useMutation({
    mutationFn: async ({ id, patch }: { id: number; patch: Partial<Pick<CockpitUser, "full_name" | "email" | "role" | "is_active">> }) => {
      const res = await fetch(`/api/cockpit/settings/users/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error("User update failed");
      return res.json() as Promise<{ user: CockpitUser }>;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["settings", "users"] }),
  });
}

export function useResetUserPassword() {
  const headers = useAuthHeaders();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/cockpit/settings/users/${id}/reset-password`, {
        method: "POST",
        headers,
      });
      if (!res.ok) throw new Error("Password reset failed");
      return res.json() as Promise<{ temporary_password: string }>;
    },
  });
}
